import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import type {
  CampaignGoodsArrival,
  CampaignGoodsArrivalScope,
  FulfillmentAdminClient,
} from '@/infrastructure/FulfillmentAdminClient';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { ROUTES } from '@/routes/routes';
import { asciiScanField } from './asciiScanField';
import { CameraScanField } from './CameraScanField';
import { BoxIcon, CheckIcon, WarningIcon } from './UltimateIcons';
import { ULTIMATE_MODAL } from './ultimateModal';
import '../ultimateDesign.css';
import './campaignGoodsArrival.css';

/** The server refuses a longer shelf code, so the box stops there instead of the request. */
const SHELF_MAX_LENGTH = 64;

const TITLE_ID = 'campaign-goods-arrival-title';

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The server's answer: how many orders the goods were for, what this press received, and each line it could not. */
function ArrivalResult({ result, shelf }: { result: CampaignGoodsArrival; shelf: string }) {
  const failed = result.failures.length;
  if (result.orderCount === 0) {
    return (
      <Alert variant="warning" className="mb-0" data-testid="campaign-goods-result">
        No campaign-supplied orders here, so nothing was received. Orders taken from FF2 stock are inbounded as usual.
      </Alert>
    );
  }
  return (
    <div data-testid="campaign-goods-result">
      <div className={`cga-summary ${failed > 0 ? 'is-act' : 'is-ok'}`} role="status">
        {failed > 0 ? <WarningIcon /> : <CheckIcon />}
        <span>
          {plural(result.inbounded, 'line')} received on {shelf}
          {result.alreadyArrived > 0 ? ` · ${result.alreadyArrived} had already arrived` : ''}
          {failed > 0 ? ` · ${failed} could not be received` : ''}.
        </span>
      </div>
      <dl className="cga-counts">
        <div>
          <dt>Orders</dt>
          <dd>{result.orderCount}</dd>
        </div>
        <div>
          <dt>Received now</dt>
          <dd>{result.inbounded}</dd>
        </div>
        <div>
          <dt>Already arrived</dt>
          <dd>{result.alreadyArrived}</dd>
        </div>
        <div className={failed > 0 ? 'is-act' : undefined}>
          <dt>Failed</dt>
          <dd>{failed}</dd>
        </div>
      </dl>
      {failed > 0 && (
        <div className="cga-failures">
          <Table size="sm" className="cga-table mb-0 align-middle" aria-label="Lines not received">
            <colgroup>
              <col className="cga-order" />
              <col className="cga-line" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Order</th>
                <th>Line</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {result.failures.map((failure) => (
                <tr key={`${failure.orderId}-${failure.lineNo}`}>
                  <td>
                    <Link to={ROUTES.fulfillment.orderDetail(failure.orderId)} className="cga-oid">
                      #{failure.orderId}
                    </Link>
                  </td>
                  <td className="cga-num">{failure.lineNo}</td>
                  <td>{failure.reason}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

interface CampaignGoodsArrivalDialogProps {
  readonly show: boolean;
  /** Opened over another modal (Add orders): it sits a layer above, its backdrop dimming the modal underneath. */
  readonly stacked?: boolean | undefined;
  readonly client: FulfillmentAdminClient;
  /** The campaign or the upload batch whose campaign-supplied lines the goods are for; null while nothing is picked. */
  readonly scope: CampaignGoodsArrivalScope | null;
  /** What the header names, e.g. "campaign Glow Serum Seeding" or "import seeding-oct.xlsx". */
  readonly scopeLabel: string;
  readonly onHide: () => void;
  /** Called after the server answered, so the screen behind can read its orders again. */
  readonly onReceived?: ((result: CampaignGoodsArrival) => void) | undefined;
}

/**
 * "Campaign goods arrived" (SSD-7458): the brand's goods for a campaign, or for one marketing upload, reached the floor.
 * The desk names the shelf they were put on and every campaign-supplied line still waiting is inbounded there, without
 * booking FF2 stock. The answer is shown as counts and each line that could not be received; pressing it again is safe,
 * because a line that already arrived is counted, not received twice.
 */
export function CampaignGoodsArrivalDialog({
  show,
  stacked = false,
  client,
  scope,
  scopeLabel,
  onHide,
  onReceived,
}: CampaignGoodsArrivalDialogProps) {
  const [shelf, setShelf] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [result, setResult] = useState<CampaignGoodsArrival | null>(null);
  /** One request at a time, read synchronously: a scanner Enter and a click in one tick must not send twice. */
  const sendingRef = useRef(false);

  // Keyed by value: a caller passing a fresh `{ batchId }` each render must not wipe an answer already on screen.
  const scopeKey = scope ? ('campaignId' in scope ? `campaign:${scope.campaignId}` : `batch:${scope.batchId}`) : '';

  // Each opening starts from an empty shelf: a code left from another campaign is one Enter away from the wrong shelf.
  useEffect(() => {
    if (show) {
      setShelf('');
      setRefusal(null);
      setResult(null);
    }
  }, [show, scopeKey]);

  const shelfCode = shelf.trim();

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!scope || shelfCode === '' || sendingRef.current || result) {
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setRefusal(null);
    try {
      const answer = await client.receiveCampaignGoods(scope, shelfCode);
      setResult(answer);
      onReceived?.(answer);
    } catch (error) {
      setRefusal(describeFailure(error, 'The goods could not be received. Nothing was changed by this press.'));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  // The request writes stock-side state, so the dialog holds until the server has answered.
  const hide = () => {
    if (!sending) {
      onHide();
    }
  };

  return (
    <Modal
      {...ULTIMATE_MODAL}
      {...(stacked
        ? {
            className: `${ULTIMATE_MODAL.className} cga-stacked`,
            backdropClassName: `${ULTIMATE_MODAL.backdropClassName} cga-stacked-backdrop`,
          }
        : {})}
      show={show}
      onHide={hide}
      backdrop={sending ? 'static' : true}
      keyboard={!sending}
      dialogClassName="cga-dialog"
      aria-labelledby={TITLE_ID}
    >
      <Modal.Header closeButton={!sending}>
        <Modal.Title as="h5" id={TITLE_ID}>
          Campaign goods arrived
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={(event) => void submit(event)}>
        <Modal.Body>
          <p className="cga-scope">
            For <strong>{scopeLabel}</strong>
          </p>
          {result ? (
            <ArrivalResult result={result} shelf={shelfCode} />
          ) : (
            <>
              <Form.Group controlId="campaign-goods-shelf">
                <Form.Label>Shelf</Form.Label>
                {/* The read only fills the box: the desk confirms the shelf before anything is received [SSD-7527]. */}
                <CameraScanField label="Scan shelf with camera" ultimate enterAfterRead={false}>
                  <Form.Control
                    {...asciiScanField(setShelf)}
                    value={shelf}
                    maxLength={SHELF_MAX_LENGTH}
                    disabled={sending}
                    placeholder="e.g. MKT-01"
                    autoFocus
                  />
                </CameraScanField>
                <Form.Text className="text-muted">
                  The shelf the brand&apos;s goods were put on. Every campaign-supplied line still waiting is received
                  there; FF2 stock is not booked. Pressing it again is safe: lines that already arrived are counted, not
                  received twice.
                </Form.Text>
              </Form.Group>
              {refusal && (
                <Alert variant="danger" className="mt-3 mb-0" role="alert">
                  {refusal}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {result ? (
            <Button variant="primary" onClick={onHide}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline-secondary" disabled={sending} onClick={hide}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={sending || shelfCode === '' || !scope}>
                <BoxIcon />
                {sending ? 'Receiving…' : 'Receive goods'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
